import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Tag, X } from "lucide-react";
import { useCategorias, useCrearCategoria } from "../hooks/useCategorias";
import CategoriaCard from "../components/categorias/CategoriaCard";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

export default function CategoriasPage() {
  const navigate = useNavigate();
  const { user, isReadOnly } = useAuth();
  const academiaId = user?.academia_id || "";
  const { data: categorias = [], isLoading } = useCategorias(academiaId);
  const crearCategoria = useCrearCategoria();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    nombre: "", genero: "masculino", anio_nacimiento: "", color_hex: "#3B82F6", descripcion: ""
  });

  async function handleCrear(e: React.FormEvent) {
    e.preventDefault();
    try {
      await crearCategoria.mutateAsync({
        academia_id: academiaId,
        nombre: form.nombre,
        genero: form.genero,
        anio_nacimiento: form.anio_nacimiento ? parseInt(form.anio_nacimiento) : undefined,
        color_hex: form.color_hex,
        descripcion: form.descripcion,
      } as any);
      toast.success("Categoría creada");
      setShowForm(false);
      setForm({ nombre: "", genero: "masculino", anio_nacimiento: "", color_hex: "#3B82F6", descripcion: "" });
    } catch {
      toast.error("Error al crear categoría");
    }
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Tag size={20} className="text-violet-500"/>
              <h1 className="text-2xl font-bold text-gray-900">Categorías</h1>
            </div>
            <p className="text-gray-500 text-sm">{categorias.length} {categorias.length === 1 ? "categoría registrada" : "categorías registradas"}</p>
          </div>
          {!isReadOnly() && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <Plus size={16}/>
              Nueva categoría
            </button>
          )}
        </div>

        {/* Form modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="font-bold text-gray-900 text-lg">Nueva categoría</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100">
                  <X size={18}/>
                </button>
              </div>
              <form onSubmit={handleCrear} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Nombre *</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: U13 Masculino, Sub-15 Femenino"
                    value={form.nombre}
                    onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Género</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.genero}
                    onChange={(e) => setForm((f) => ({ ...f, genero: e.target.value }))}
                  >
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                    <option value="mixto">Mixto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Año de nacimiento</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2013"
                    value={form.anio_nacimiento}
                    onChange={(e) => setForm((f) => ({ ...f, anio_nacimiento: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Color identificador</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer p-0.5"
                      value={form.color_hex}
                      onChange={(e) => setForm((f) => ({ ...f, color_hex: e.target.value }))}
                    />
                    <span className="text-sm text-gray-500 font-mono">{form.color_hex}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Vista previa</label>
                  <div className="h-10 rounded-lg flex items-center px-3 text-white text-sm font-medium" style={{ background: form.color_hex }}>
                    {form.nombre || "Nombre"}
                  </div>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Descripción</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={2}
                    placeholder="Opcional..."
                    value={form.descripcion}
                    onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                  />
                </div>
                <div className="col-span-1 sm:col-span-2 flex gap-3 justify-end pt-2">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
                    Cancelar
                  </button>
                  <button type="submit" disabled={crearCategoria.isPending}
                    className="px-5 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm">
                    {crearCategoria.isPending ? "Guardando..." : "Crear categoría"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Grid de categorías */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-3"/>
                <div className="h-3 bg-gray-100 rounded w-1/2"/>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categorias.map((cat) => (
              <CategoriaCard
                key={cat.id}
                categoria={cat}
                onClick={() => navigate(`/categorias/${cat.id}`)}
              />
            ))}
            {categorias.length === 0 && (
              <div className="col-span-3">
                <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
                  <Tag size={40} className="text-gray-300 mx-auto mb-3"/>
                  <p className="text-gray-500 font-medium mb-1">Sin categorías</p>
                  <p className="text-gray-400 text-sm mb-4">Crea la primera categoría del plantel</p>
                  {!isReadOnly() && (
                    <button onClick={() => setShowForm(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                      + Nueva categoría
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

// Obtener academiaId del token guardado (simplificado)
function getAcademiaId(): string {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.academia_id ?? "";
  } catch {
    return "";
  }
}

